import {type FunctionalComponent, h} from '@stencil/core';
import {DashboardDocumentsTile} from '../dashbord-documents/dashboard-documents-tile';
import {DashboardCompanyTile} from '../dashboard-company-tile/dashboard-company-tile';
import {DashboardProducts} from '../dashboard-products/dashboard-products';
import type {Strategies} from '../../../../../strategies/Strategies';
import type {ResultRenderer} from '../../../../../../utils/Renderer';
import {NoResults} from '../../no-results/no-results';
import type {SearchApiDocument, SearchDocument, SearchProduct,} from '../../../../../../api/SearchApi';
import {capitalizeText} from '../utils/utils';
import type {IEolDiscontinuedDetails, LayoutItem} from '../../types/subcomponents-types.type';
import type {
  IWidgetRequestData
} from '../../../../../widgets/offsites-widget/offsites-widget-v4/types/offsites-widget-v4.types';
import ConversationalBanner from './dashboard sections/ConversationalBanner';
import FaqDashboardWidget from './dashboard sections/FaqDashboardWidget';
import {
  TILE_BLOG,
  TILE_COMPANY,
  TILE_DOCUMENTS,
  TILE_SERVICES,
  TILE_SOFTWARE,
  TILE_SOLUTIONS,
  TILE_SUPPORT,
  TILE_SUPPORT_COMMUNITY,
  TILE_SUPPORT_VIDEO
} from '../../../../../variables';
import {createDashboardLayout } from './create-dashboard-grid';
import {CtaPublicWidgetsRow} from '../dashboard-widgets/cta-public-widgets-row';
import {CtaPublicDesignFirmWidgetsRow} from '../dashboard-widgets/cta-public-design-firm-widgets-row';
import {DashboardOffsitesV3} from '../dashboard-widgets/dashboard-offsites-v3';
import {DashboardOffsitesV4} from '../dashboard-widgets/dashboard-offsites-v4';
import {DashboardExchangeCommunityWidget} from '../dashboard-widgets/dashboard-exchange-community-widget';
import type {MiaFiltersDetails} from '../../../../../widgets/filter-related-widget/additional-conditions-banner/types';
import {SupportChatWidget} from '../dashboard-widgets/support-chat-widget';
import type {WCEventData} from '../../../../../../api/model/response/documents/SearchResult';

export interface IDashboardDesktopView {
  apigeeTokenUrl?: string;
  apigeeAuth?: string;
  isDashboardView: boolean;
  reflectedTabs: Object;
  searcherNames: any;//eslint-disable-line @typescript-eslint/no-explicit-any
  strategies: Strategies;
  resultRenderer: ResultRenderer;
  resultClickAnalytics: CallableFunction;
  labelProvider: CallableFunction;
  country: string;
  language: string;
  tabName: string;
  isEcommerceMode: boolean;
  eShop: boolean;
  rtl: boolean;
  isOffsitesVisible: boolean;
  isExchangeVisible: boolean;
  widgetType: string;
  queryProp: string;
  hasCtaProductSelector: boolean;
  hasSupportButton: boolean;
  faqData;
  query: string;
  getTabCount: CallableFunction;
  relatedProductName?: string;
  smartCountrySelectorElement: HTMLElement | string;
  productCountrySelectorElement: HTMLElement | string;
  totalHits: number;
  productNaCountrySelectorLocales: string;
  isProductNaCountrySelector?: boolean;
  globalDecommissioning: boolean;
  isConversationalSearchActive?: boolean;
  conversationalSearchMedaliaFormId?: string;
  analyticsTabName: string;
  offsitesViewOption?: string | undefined;
  dashboardButtonText: string;
  dashboardSubTitleText: string;
  lazyLoadImages?: boolean;
  brand: string;
  sectionsData?: {
    [key: string]:
      | SearchApiDocument[]
      | SearchDocument[]
      | SearchProduct[];
  };
  isSupport?: boolean;
  isAdditionalConditionsWidgetActive?: boolean;
  isMiaPromotionWidget?: boolean;
  miaData?: MiaFiltersDetails;
  isSubstitutionTool?: boolean;
  eolDiscontinuedResults?: IEolDiscontinuedDetails;
  conversationalSearchProductsEndpoint?: string;
  conversationalSearchBrandUrl?: string;
  offsiteBannersManagerRequestData?: IWidgetRequestData;
  dynamicBannerActiveByDefault?: boolean;
  isDesignFirm?: boolean;
  tabs?: string[];
  layout?: LayoutItem[];
  wcEventData?: WCEventData;
  isUnifiedCheckout?: boolean;
}

export const DashboardDesktopView: FunctionalComponent<
  IDashboardDesktopView
> = (props) => {
    const {
      reflectedTabs,
      searcherNames,
      labelProvider,
      country,
      language,
      queryProp,
      getTabCount,
      analyticsTabName,
      tabName,
      smartCountrySelectorElement,
      productCountrySelectorElement,
      productNaCountrySelectorLocales,
      isProductNaCountrySelector,
      totalHits,
      sectionsData,
      eolDiscontinuedResults,
      apigeeAuth,
      apigeeTokenUrl,
      conversationalSearchProductsEndpoint,
      offsiteBannersManagerRequestData,
      conversationalSearchBrandUrl,
      conversationalSearchMedaliaFormId,
      globalDecommissioning,
      tabs,
      layout,
      brand,
      rtl,
      wcEventData,
      isUnifiedCheckout
    } = props;

    const dashboardState = {
      tab: searcherNames.all,
      ajaxIsLoading: reflectedTabs?.[searcherNames.all]?.ajaxIsLoading,
      hasError: reflectedTabs?.[searcherNames.all]?.hasError,
      count: reflectedTabs?.[searcherNames.all]?.count,
    };
    const renderError = !reflectedTabs?.[searcherNames.all]?.ajaxIsLoading && reflectedTabs?.[searcherNames.all]?.hasError;
    const renderNoResults = reflectedTabs?.[searcherNames.all]?.count === 0 && !renderError;


    const sectionIsLoading = (section: string):boolean => {
        if (!tabs.includes(section)) return false;
        if (reflectedTabs?.[searcherNames[section]]?.hasError) return false;
        return reflectedTabs?.[searcherNames[section]]?.count === undefined;
    };

    const shouldRenderResults = (): boolean => {
        return Object.keys(searcherNames).some(section => reflectedTabs?.[searcherNames[section]]?.count > 0);
    };

    const isProductData = sectionsData?.products?.length || eolDiscontinuedResults?.documents?.length || eolDiscontinuedResults?.documentsGroup?.length;
    const isProductDataStatement = isProductData > 0;

    const limitSupportVideoResults = (): number | null => {
        if (!sectionsData) return null;
        const filteredSections = Object.entries(sectionsData)
          .filter(([key, value]) => key !== 'products' && key !== 'faq' && key !== 'support' && value?.length > 0)
          .map(([, value]) => value);
        return filteredSections.length % 2 === 0 ? 2 : null;
    };
    const getTileProps = (section: string) => {
        return {
          doc: sectionsData?.[section],
          title: labelProvider('tab.se/' + section, capitalizeText(section)),
          targetTab: searcherNames?.[section],
          tabCount: getTabCount(searcherNames?.[section]),
          useSkeleton: true,
          sectionIsLoading: sectionIsLoading(section),
          section,
          ...props
        };
    };

    const componentMap = {
      SupportChatWidget: <SupportChatWidget term={queryProp} language={language} country={country}/>,
      DocumentsTile: <DashboardDocumentsTile {...getTileProps(TILE_DOCUMENTS)} />,
      ConversationalBanner: <ConversationalBanner {...props} isVisible={isProductDataStatement} />,
      SmartCountrySelector: smartCountrySelectorElement,
      ProductNaCountrySelector: productCountrySelectorElement,
      ProductsCarousel: <DashboardProducts
        {...props}
        tabName={reflectedTabs?.[searcherNames?.products]?.tab}
        products={sectionsData?.products as SearchProduct[]}
        tabCount={reflectedTabs?.[searcherNames?.products]?.count}
        targetTab={searcherNames.products}
        subTitle={true}
        isProductData={isProductDataStatement}
        useSkeleton={true}
        sectionIsLoading={sectionIsLoading('products')}
      />,
      FaqWidget: <FaqDashboardWidget targetTab={searcherNames.faq} tabCount={getTabCount(searcherNames.faq)} {...props}/>,
      SoftwareTile: <DashboardDocumentsTile {...getTileProps(TILE_SOFTWARE)}/>,
      CompanyTile: <DashboardCompanyTile {...getTileProps(TILE_COMPANY)} />,
      ServicesTile: <DashboardDocumentsTile {...getTileProps(TILE_SERVICES)} />,
      SolutionsTile: <DashboardDocumentsTile {...getTileProps(TILE_SOLUTIONS)}/>,
      SupportTile: <DashboardDocumentsTile {...getTileProps(TILE_SUPPORT)}/>,
      BlogTile: <DashboardCompanyTile {...getTileProps(TILE_BLOG)} />,
      SupportCommunityTile: <DashboardDocumentsTile {...getTileProps(TILE_SUPPORT_COMMUNITY)} />,
      SupportVideoTile: <DashboardCompanyTile {...getTileProps(TILE_SUPPORT_VIDEO)} limitResultsNumber={limitSupportVideoResults()}/>,
      CtaDesignFirmWidgets: <CtaPublicDesignFirmWidgetsRow {...props} />,
      OffsitesWidgetV4: <DashboardOffsitesV4 {...props}/>,
      OffsitesWidgetV3: <DashboardOffsitesV3 {...props}/>,
      ExchangeCommunityWidget: <DashboardExchangeCommunityWidget {...props} strategies={getTileProps(TILE_SUPPORT_COMMUNITY).strategies}/>,
      CtaPublicWidgets:<CtaPublicWidgetsRow {...props}/>
    };

    return [
      renderError && (
        <uiaas-search-server-error
          query={queryProp}
          labelProvider={(e, f) => labelProvider(e, f)}
          reflectedState={dashboardState}
        />
      ),
      shouldRenderResults() ? (
        <div class='dashboard-grid'>
          {createDashboardLayout(layout, componentMap)}
        </div>
      ) : renderNoResults ? (
        <div>
          <NoResults
            {...props}
            loading={reflectedTabs?.[searcherNames.all]?.ajaxIsLoading}
            labelProvider={(e, f) => labelProvider(e, f)}
            query={queryProp}
          />
        </div>
      ) : (
        ''
      ),
    ];
};
